import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  Scale, 
  Download, 
  Share2, 
  Heart,
  Gauge,
  Fuel,
  Cog,
  Weight,
  Zap,
  ChevronRight,
  ChevronLeft,
  FileText,
  Phone,
  Bitcoin,
  Clock,
  CheckCircle,
  MapPin,
  Ruler,
  Package,
  Settings,
  Calendar,
  Hash,
  Mail,
  MessageSquare,
  Printer,
  ZoomIn
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCompare } from '@/context/CompareContext';
import { toast } from 'sonner';
import EngineCard from '@/components/EngineCard';
import { getEngineImage, getImageUrl } from '@/utils/imageUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EngineDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [engine, setEngine] = useState(null);
  const [relatedEngines, setRelatedEngines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showZoom, setShowZoom] = useState(false);
  const { addToCompare, isInCompare, removeFromCompare } = useCompare();
  const inCompare = engine ? isInCompare(engine.id) : false;

  useEffect(() => {
    fetchEngine();
  }, [id]);

  const fetchEngine = async () => {
    setLoading(true);
    try {
      const [engineRes, relatedRes] = await Promise.all([
        axios.get(`${API_URL}/api/engines/${id}`),
        axios.get(`${API_URL}/api/engines/${id}/related`)
      ]);
      setEngine(engineRes.data);
      setRelatedEngines(relatedRes.data);
    } catch (error) {
      console.error('Failed to fetch engine:', error);
      toast.error('Failed to load engine details');
    } finally {
      setLoading(false);
    }
  };

  const handleCompare = () => {
    if (inCompare) {
      removeFromCompare(engine.id);
    } else {
      addToCompare(engine);
    }
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard');
  };

  const handlePrint = () => {
    window.print();
  };

  const nextImage = () => {
    if (engine?.images?.length > 1) {
      setSelectedImage((prev) => (prev + 1) % engine.images.length);
    }
  };

  const prevImage = () => {
    if (engine?.images?.length > 1) {
      setSelectedImage((prev) => (prev - 1 + engine.images.length) % engine.images.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!engine) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Engine Not Found</h2>
          <Link to="/inventory">
            <Button>Back to Inventory</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Condition badge color
  const conditionColors = {
    'New': 'bg-emerald-500',
    'Like New': 'bg-sky-500',
    'Rebuilt': 'bg-blue-500',
    'Good Used': 'bg-amber-500',
    'High Hour': 'bg-orange-600',
  };

  return (
    <main className="min-h-screen bg-slate-100" data-testid="engine-detail-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="container-custom py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Link to="/" className="hover:text-orange-500">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/inventory" className="hover:text-orange-500">Inventory</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to={`/inventory?brand=${engine.brand}`} className="hover:text-orange-500">{engine.brand}</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-slate-900 font-medium truncate max-w-[200px]">{engine.model}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={handlePrint} className="text-slate-500">
                <Printer className="w-4 h-4 mr-1" />
                Print
              </Button>
              <Button variant="ghost" size="sm" onClick={handleShare} className="text-slate-500">
                <Share2 className="w-4 h-4 mr-1" />
                Share
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container-custom py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* LEFT SIDE - Images */}
          <div className="space-y-4">
            {/* Main Image with Navigation */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden relative group">
              <div className="aspect-[4/3] relative">
                <img
                  src={getEngineImage(engine, selectedImage)}
                  alt={engine.title}
                  className="w-full h-full object-cover cursor-zoom-in"
                  onClick={() => setShowZoom(true)}
                  data-testid="main-image"
                />
                
                {/* Badges */}
                <div className="absolute top-4 left-4 flex flex-col gap-2">
                  {engine.condition && (
                    <Badge className={`${conditionColors[engine.condition] || 'bg-slate-500'} text-white`}>
                      {engine.condition}
                    </Badge>
                  )}
                  {engine.is_featured && <Badge className="bg-orange-500">Featured</Badge>}
                  {engine.is_bestseller && <Badge className="bg-purple-500">Bestseller</Badge>}
                </div>

                {/* Item Number */}
                {engine.item_number && (
                  <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded text-sm font-mono">
                    Item #{engine.item_number}
                  </div>
                )}

                {/* Zoom Indicator */}
                <div className="absolute bottom-4 right-4 bg-black/50 text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                  <ZoomIn className="w-5 h-5" />
                </div>

                {/* Navigation Arrows */}
                {engine.images?.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}
              </div>

              {/* Image Counter */}
              {engine.images?.length > 1 && (
                <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1 rounded text-sm">
                  {selectedImage + 1} / {engine.images.length}
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {engine.images?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {engine.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === idx ? 'border-orange-500 ring-2 ring-orange-200' : 'border-slate-200 hover:border-slate-400'
                    }`}
                  >
                    <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Quick Info Cards */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-md">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Gauge className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Horsepower</p>
                    <p className="font-bold text-slate-900">{engine.horsepower?.toLocaleString()} HP</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-md">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Hours</p>
                    <p className="font-bold text-slate-900">{engine.hours || 'N/A'}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-md">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Fuel className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Fuel Type</p>
                    <p className="font-bold text-slate-900">{engine.fuel_type || 'Diesel'}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-md">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Cog className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Cylinders</p>
                    <p className="font-bold text-slate-900">{engine.cylinders || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE - Details & Tabs */}
          <div className="space-y-6">
            {/* Title & Price Card */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              {/* Brand */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-semibold text-orange-600">{engine.brand}</span>
                <span className="text-slate-300">|</span>
                <span className="text-sm text-slate-500">{engine.model}</span>
                {engine.tier_rating && (
                  <>
                    <span className="text-slate-300">|</span>
                    <Badge variant="outline" className="text-xs">{engine.tier_rating}</Badge>
                  </>
                )}
              </div>

              {/* Title */}
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4" data-testid="engine-title">
                {engine.title}
              </h1>

              {/* Price */}
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-4 mb-6">
                {engine.price ? (
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Price</p>
                      <p className="text-4xl font-bold text-slate-900" data-testid="engine-price">
                        ${engine.price.toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">BTC equivalent</p>
                      <p className="text-lg font-mono text-orange-600">
                        ≈ {(engine.price / 97500).toFixed(4)} BTC
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-lg text-slate-600">Contact us for pricing</p>
                )}
              </div>

              {/* CTA Buttons */}
              <div className="space-y-3">
                {engine.price && (
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg font-semibold"
                    onClick={() => navigate(`/checkout/${engine.id}`)}
                    data-testid="buy-now-btn"
                  >
                    <Bitcoin className="w-5 h-5 mr-2" />
                    Buy Now with BTC
                  </Button>
                )}
                
                <div className="grid grid-cols-2 gap-3">
                  <Link to={`/contact?engine=${engine.id}`} className="block">
                    <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white py-5" data-testid="request-quote-btn">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Request More Details
                    </Button>
                  </Link>
                  <a href="tel:+12012344851">
                    <Button variant="outline" className="w-full py-5 border-2 hover:bg-slate-50">
                      <Phone className="w-4 h-4 mr-2" />
                      Call For A Quote
                    </Button>
                  </a>
                </div>

                <div className="flex gap-3">
                  <Button 
                    variant={inCompare ? "default" : "outline"} 
                    onClick={handleCompare}
                    className={`flex-1 ${inCompare ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
                    data-testid="compare-btn"
                  >
                    <Scale className="w-4 h-4 mr-2" />
                    {inCompare ? 'Added to Compare' : 'Add to Compare'}
                  </Button>
                  <Button variant="outline" onClick={handleShare}>
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Tabbed Specifications */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <Tabs defaultValue="specifications" className="w-full">
                <TabsList className="w-full grid grid-cols-3 bg-slate-100 rounded-none p-0 h-auto">
                  <TabsTrigger 
                    value="specifications" 
                    className="py-4 rounded-none data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Specifications
                  </TabsTrigger>
                  <TabsTrigger 
                    value="dimensions"
                    className="py-4 rounded-none data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold"
                  >
                    <Ruler className="w-4 h-4 mr-2" />
                    Dim & Logistics
                  </TabsTrigger>
                  <TabsTrigger 
                    value="notes"
                    className="py-4 rounded-none data-[state=active]:bg-white data-[state=active]:shadow-sm font-semibold"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Notes
                  </TabsTrigger>
                </TabsList>
                
                {/* Specifications Tab */}
                <TabsContent value="specifications" className="p-6 m-0">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <SpecRow label="Brand" value={engine.brand} />
                      <SpecRow label="Model" value={engine.model} />
                      <SpecRow label="Condition" value={engine.condition} highlight />
                      <SpecRow label="Hours" value={engine.hours || 'N/A'} />
                      <SpecRow label="Horsepower" value={`${engine.horsepower?.toLocaleString()} HP`} />
                      <SpecRow label="RPM" value={engine.rpm_range || `${engine.rpm} RPM`} />
                      <SpecRow label="Cylinders" value={engine.cylinders} />
                      <SpecRow label="Displacement" value={engine.displacement ? `${engine.displacement}L` : 'N/A'} />
                      <SpecRow label="Fuel Type" value={engine.fuel_type || 'Diesel'} />
                      <SpecRow label="Engine Type" value={engine.engine_type} />
                      <SpecRow label="Tier Rating" value={engine.tier_rating || 'N/A'} />
                      <SpecRow label="Serial Number" value={engine.serial_number || 'Available on request'} />
                    </div>
                    
                    {/* Additional Specs from specifications object */}
                    {engine.specifications && Object.keys(engine.specifications).length > 0 && (
                      <div className="border-t border-slate-100 pt-4 mt-4">
                        <h4 className="font-semibold text-slate-700 mb-3">Additional Details</h4>
                        <div className="grid grid-cols-2 gap-4">
                          {Object.entries(engine.specifications).map(([key, value]) => (
                            <SpecRow key={key} label={key.replace(/_/g, ' ')} value={value} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>
                
                {/* Dimensions & Logistics Tab */}
                <TabsContent value="dimensions" className="p-6 m-0">
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <Package className="w-4 h-4 text-orange-500" />
                        Dimensions & Weight
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <SpecRow label="Length" value={engine.dimensions?.length || 'Contact for details'} />
                        <SpecRow label="Width" value={engine.dimensions?.width || 'Contact for details'} />
                        <SpecRow label="Height" value={engine.dimensions?.height || 'Contact for details'} />
                        <SpecRow label="Dry Weight" value={engine.dimensions?.dry_weight || (engine.weight ? `${engine.weight} kg` : 'Contact for details')} />
                        <SpecRow label="Wet Weight" value={engine.dimensions?.wet_weight || 'Contact for details'} />
                      </div>
                    </div>
                    
                    <div className="border-t border-slate-100 pt-4">
                      <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-orange-500" />
                        Shipping Information
                      </h4>
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-sm text-blue-800 mb-2">
                          <strong>Ships from:</strong> Qinhuangdao, China or Port Reading, NJ, USA
                        </p>
                        <p className="text-sm text-blue-700">
                          Ocean freight available worldwide. Shipping costs calculated at checkout based on destination and container requirements.
                        </p>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                {/* Notes Tab */}
                <TabsContent value="notes" className="p-6 m-0">
                  <div className="space-y-4">
                    {engine.notes ? (
                      <div className="prose prose-slate max-w-none">
                        <p className="text-slate-600 whitespace-pre-wrap">{engine.notes}</p>
                      </div>
                    ) : (
                      <p className="text-slate-500">No additional notes available for this engine.</p>
                    )}
                    
                    {engine.description && (
                      <div className="border-t border-slate-100 pt-4 mt-4">
                        <h4 className="font-semibold text-slate-700 mb-3">Description</h4>
                        <p className="text-slate-600">{engine.description}</p>
                      </div>
                    )}

                    {/* Features */}
                    {engine.features?.length > 0 && (
                      <div className="border-t border-slate-100 pt-4 mt-4">
                        <h4 className="font-semibold text-slate-700 mb-3">Features</h4>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {engine.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-slate-600 text-sm">
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Downloads */}
            {engine.manuals?.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Download className="w-5 h-5 text-orange-500" />
                  Downloads & Manuals
                </h3>
                <div className="space-y-2">
                  {engine.manuals.map((manual, idx) => (
                    <a
                      key={idx}
                      href={manual}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group"
                    >
                      <FileText className="w-5 h-5 text-orange-500" />
                      <span className="flex-1 text-sm group-hover:text-orange-600">Manual {idx + 1}</span>
                      <Download className="w-4 h-4 text-slate-400 group-hover:text-orange-500" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Card */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-6 text-white">
              <h3 className="font-semibold text-lg mb-2">Need More Information?</h3>
              <p className="text-slate-300 text-sm mb-4">
                Our team is ready to answer your questions about this engine.
              </p>
              <div className="flex flex-col gap-2">
                <a href="tel:+12012344851" className="flex items-center gap-2 text-orange-400 hover:text-orange-300">
                  <Phone className="w-4 h-4" />
                  +1 (201) 234-4851
                </a>
                <a href="mailto:sales@engineshippart.com" className="flex items-center gap-2 text-orange-400 hover:text-orange-300">
                  <Mail className="w-4 h-4" />
                  sales@engineshippart.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Related Engines */}
        {relatedEngines.length > 0 && (
          <div className="mt-16">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-slate-900">Similar Engines</h2>
              <Link to={`/inventory?brand=${engine.brand}`}>
                <Button variant="outline">
                  View All {engine.brand}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedEngines.slice(0, 4).map((eng) => (
                <EngineCard key={eng.id} engine={eng} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Image Zoom Modal */}
      {showZoom && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setShowZoom(false)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-orange-500"
            onClick={() => setShowZoom(false)}
          >
            <span className="text-2xl">×</span>
          </button>
          <img
            src={getEngineImage(engine, selectedImage)}
            alt={engine.title}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}
    </main>
  );
}

// Spec Row Component
function SpecRow({ label, value, highlight = false }) {
  return (
    <div className={`flex justify-between py-2 border-b border-slate-100 ${highlight ? 'bg-orange-50 -mx-2 px-2 rounded' : ''}`}>
      <span className="text-slate-500 text-sm">{label}</span>
      <span className={`font-medium text-sm ${highlight ? 'text-orange-600' : 'text-slate-900'}`}>{value}</span>
    </div>
  );
}
